import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  api,
  type ManagedTheme,
  type ThemeStatus,
} from "../lib/api";
import { Panel } from "../components/salience/Panel";

/**
 * Phase 8 — theme management.
 *
 * Curator view of the `themes` table. Three sections (Active / Suggested /
 * Disabled), per-row activate / suggest / disable / edit, plus a manual
 * "+ Add theme" form. The 10-active hard limit is enforced server-side;
 * the UI just surfaces the 409 response inline on the row that triggered it.
 *
 * Dark-terminal register (portal-wide design unification, 2026-06-12) —
 * sections are salience Panels, rows are terminal-panel cards.
 */

const STATUS_ORDER: ThemeStatus[] = ["active", "suggested", "disabled"];
const SECTION_TITLE: Record<ThemeStatus, string> = {
  active: "Active picklist",
  suggested: "Suggested",
  disabled: "Disabled",
};
const SECTION_BLURB: Record<ThemeStatus, string> = {
  active:
    "The themes the AI currently tags new comments against. There's a hard limit, so disable one before turning on another.",
  suggested:
    "Possible themes, either proposed automatically or added by hand. Turn on the ones worth tracking; disable the rest to tidy up.",
  disabled:
    "Themes the AI no longer tags. Kept for the record, and can be suggested or turned back on later.",
};

export function ThemesManager() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["themes-manage"],
    queryFn: () => api.themesManage.list(),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["themes-manage"] });

  const patchMutation = useMutation({
    mutationFn: (args: {
      themeId: string;
      patch: Partial<{
        label: string;
        description: string;
        status: ThemeStatus;
      }>;
    }) => api.themesManage.patch(args.themeId, args.patch),
    onSuccess: invalidate,
  });

  const createMutation = useMutation({
    mutationFn: api.themesManage.create,
    onSuccess: invalidate,
  });

  const grouped = useMemo(() => {
    const out: Record<ThemeStatus, ManagedTheme[]> = {
      active: [],
      suggested: [],
      disabled: [],
    };
    (data?.themes ?? []).forEach((t) => out[t.status]?.push(t));
    for (const k of STATUS_ORDER) {
      out[k].sort((a, b) =>
        a.theme_id < b.theme_id ? -1 : a.theme_id > b.theme_id ? 1 : 0,
      );
    }
    return out;
  }, [data]);

  const activeCount = grouped.active.length;
  const activeLimit = data?.active_limit ?? 10;
  const atLimit = activeCount >= activeLimit;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-64 animate-pulse rounded bg-black/5" />
        <div className="h-32 animate-pulse rounded-xl bg-terminal-panel/50" />
        <div className="h-64 animate-pulse rounded-xl bg-terminal-panel/50" />
      </div>
    );
  }
  if (error) {
    return (
      <Panel title="Couldn't load themes">
        <p className="text-sm text-terminal-ink-muted">
          We couldn't load the themes right now. Please refresh to try again.
        </p>
        <p className="mt-2 font-mono text-xs text-terminal-ink-muted">
          {String(error)}
        </p>
      </Panel>
    );
  }

  return (
    <div className="space-y-6">
      <header>
        <div className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
          YouTube audience · Agent 1
        </div>
        <h1 className="mt-1 text-2xl font-bold tracking-tight text-ink">
          Theme management
        </h1>
        <p className="mt-1.5 text-sm text-ink-muted">
          Choose which themes the AI tags comments against. Active count:{" "}
          <span
            className={
              atLimit ? "font-semibold text-down" : "font-semibold text-ink"
            }
          >
            {activeCount} / {activeLimit}
          </span>
          {atLimit && (
            <span className="ml-2 text-down">
              · at the limit. Disable an active theme before turning on a new one.
            </span>
          )}
        </p>
      </header>

      <AddThemeForm
        onSubmit={(body) => createMutation.mutateAsync(body)}
        pending={createMutation.isPending}
        lastError={createMutation.error as Error | null}
      />

      {STATUS_ORDER.map((status) => (
        <Panel
          key={status}
          title={SECTION_TITLE[status]}
          eyebrow={`${grouped[status].length} theme${grouped[status].length === 1 ? "" : "s"}`}
          hint={SECTION_BLURB[status]}
        >
          {grouped[status].length === 0 ? (
            <p className="text-sm italic text-terminal-ink-muted">
              {status === "active"
                ? "No active themes yet. Turn one on from the Suggested list below."
                : status === "suggested"
                  ? "No suggestions yet. Add one manually below."
                  : "Nothing disabled."}
            </p>
          ) : (
            <ul className="space-y-2">
              {grouped[status].map((t) => (
                <ThemeRow
                  key={t.theme_id}
                  theme={t}
                  atActiveLimit={atLimit}
                  onPatch={(themeId, patch) =>
                    patchMutation.mutateAsync({ themeId, patch })
                  }
                  patchPending={patchMutation.isPending}
                />
              ))}
            </ul>
          )}
        </Panel>
      ))}
    </div>
  );
}

const INPUT_CLASSES =
  "w-full rounded border border-white/15 bg-terminal-panel px-2 py-1 text-sm text-terminal-ink placeholder:text-terminal-ink-muted/60 focus:border-white/40 focus:outline-none";

const PRIMARY_BUTTON =
  "rounded bg-agenda-public/20 px-3 py-1 font-semibold text-[#8ab4ff] transition-colors hover:bg-agenda-public/30 disabled:opacity-40";

function ThemeRow({
  theme,
  atActiveLimit,
  onPatch,
  patchPending,
}: {
  theme: ManagedTheme;
  atActiveLimit: boolean;
  onPatch: (
    themeId: string,
    patch: Partial<{ label: string; description: string; status: ThemeStatus }>,
  ) => Promise<unknown>;
  patchPending: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(theme.label);
  const [description, setDescription] = useState(theme.description);
  const [rowError, setRowError] = useState<string | null>(null);

  const cannotActivate = theme.status !== "active" && atActiveLimit;

  const setStatus = async (next: ThemeStatus) => {
    setRowError(null);
    try {
      await onPatch(theme.theme_id, { status: next });
    } catch (err) {
      setRowError((err as Error).message);
    }
  };

  const saveEdit = async () => {
    setRowError(null);
    try {
      await onPatch(theme.theme_id, { label, description });
      setEditing(false);
    } catch (err) {
      setRowError((err as Error).message);
    }
  };

  return (
    <li className="rounded-lg border border-white/10 bg-terminal-panel p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <code className="font-mono text-xs text-terminal-ink-muted">
            {theme.theme_id}
          </code>
          {editing ? (
            <div className="mt-1 space-y-2">
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                className={INPUT_CLASSES}
                placeholder="Short label"
              />
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={INPUT_CLASSES}
                rows={2}
                placeholder="One-sentence definition for the LLM tagger"
              />
            </div>
          ) : (
            <>
              <div className="font-medium text-terminal-ink">{theme.label}</div>
              {theme.description && (
                <p className="mt-1 text-sm text-terminal-ink-muted">
                  {theme.description}
                </p>
              )}
            </>
          )}
          <ProvenanceLine theme={theme} />
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1 text-xs">
          {editing ? (
            <>
              <button
                onClick={saveEdit}
                disabled={patchPending}
                className={PRIMARY_BUTTON}
              >
                {patchPending ? "Saving…" : "Save"}
              </button>
              <button
                onClick={() => {
                  setEditing(false);
                  setLabel(theme.label);
                  setDescription(theme.description);
                  setRowError(null);
                }}
                className="text-terminal-ink-muted transition-colors hover:text-terminal-ink"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setEditing(true)}
                className="text-terminal-ink-muted transition-colors hover:text-terminal-ink"
              >
                Edit
              </button>
              {theme.status !== "active" && (
                <button
                  onClick={() => setStatus("active")}
                  disabled={cannotActivate || patchPending}
                  className="rounded bg-up/15 px-3 py-1 font-semibold text-up transition-colors hover:bg-up/25 disabled:opacity-40"
                  title={cannotActivate ? "At the active limit. Disable one first." : undefined}
                >
                  Activate
                </button>
              )}
              {theme.status !== "suggested" && (
                <button
                  onClick={() => setStatus("suggested")}
                  disabled={patchPending}
                  className="text-terminal-ink-muted transition-colors hover:text-terminal-ink"
                >
                  Suggest
                </button>
              )}
              {theme.status !== "disabled" && (
                <button
                  onClick={() => setStatus("disabled")}
                  disabled={patchPending}
                  className="text-terminal-ink-muted transition-colors hover:text-terminal-ink"
                >
                  Disable
                </button>
              )}
            </>
          )}
        </div>
      </div>
      {rowError && <p className="mt-2 text-xs text-down">{rowError}</p>}
    </li>
  );
}

function ProvenanceLine({ theme }: { theme: ManagedTheme }) {
  const bits: string[] = [];
  bits.push(`source: ${theme.source}`);
  if (theme.source_category) bits.push(`from category: ${theme.source_category}`);
  if (theme.cluster_size) bits.push(`cluster size: ${theme.cluster_size}`);
  if (theme.seed_origin) bits.push(`seed: ${theme.seed_origin}`);
  return (
    <p className="mt-2 text-[11px] text-terminal-ink-muted/80">
      {bits.join(" · ")}
    </p>
  );
}

function AddThemeForm({
  onSubmit,
  pending,
  lastError,
}: {
  onSubmit: (body: {
    theme_id: string;
    label: string;
    description?: string;
    status?: ThemeStatus;
  }) => Promise<unknown>;
  pending: boolean;
  lastError: Error | null;
}) {
  const [open, setOpen] = useState(false);
  const [themeId, setThemeId] = useState("");
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [createActive, setCreateActive] = useState(false);

  if (!open) {
    return (
      <div>
        <button
          onClick={() => setOpen(true)}
          className="rounded-lg border border-white/10 bg-terminal-panel px-3 py-2 text-sm font-semibold text-terminal-ink-muted transition-colors hover:text-terminal-ink"
        >
          + Add theme manually
        </button>
      </div>
    );
  }

  const submit = async () => {
    try {
      await onSubmit({
        theme_id: themeId.trim(),
        label: label.trim(),
        description: description.trim(),
        status: createActive ? "active" : "suggested",
      });
      setThemeId("");
      setLabel("");
      setDescription("");
      setCreateActive(false);
      setOpen(false);
    } catch {
      /* error rendered by lastError below */
    }
  };

  return (
    <Panel title="Add a theme">
      <div className="space-y-3">
        <label className="block">
          <span className="block text-xs font-medium text-terminal-ink-muted">
            theme_id (lowercase, underscores, no spaces, e.g. wealth_tax_implementation)
          </span>
          <input
            value={themeId}
            onChange={(e) => setThemeId(e.target.value.toLowerCase())}
            className={`mt-1 ${INPUT_CLASSES}`}
            pattern="[a-z0-9_]{1,64}"
          />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-terminal-ink-muted">
            Label (short human-readable name)
          </span>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className={`mt-1 ${INPUT_CLASSES}`}
          />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-terminal-ink-muted">
            Description (one sentence describing the theme; the AI uses this to tag comments)
          </span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={`mt-1 ${INPUT_CLASSES}`}
            rows={2}
          />
        </label>
        <label className="flex items-center gap-2 text-sm text-terminal-ink">
          <input
            type="checkbox"
            checked={createActive}
            onChange={(e) => setCreateActive(e.target.checked)}
          />
          Activate immediately (subject to 10-active limit)
        </label>
        {lastError && <p className="text-xs text-down">{lastError.message}</p>}
        <div className="flex gap-2">
          <button
            onClick={submit}
            disabled={pending || !themeId || !label}
            className={`${PRIMARY_BUTTON} text-sm`}
          >
            {pending ? "Creating…" : "Create"}
          </button>
          <button
            onClick={() => setOpen(false)}
            className="text-sm text-terminal-ink-muted transition-colors hover:text-terminal-ink"
          >
            Cancel
          </button>
        </div>
      </div>
    </Panel>
  );
}
